import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Volume2, Plus, BookOpen, ChevronRight, MoreVertical, Pin, PinOff, Edit2, Trash2, FolderPlus, ArrowRightLeft, Share2, FileDown, Copy, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../../../context/ProjectContext';
import { useTheme } from '../../../../context/ThemeContext';
import { RenameChatModal, AddToProjectModal, MoveToProjectModal } from '../../../common/modals';

const ChatHeader = ({
    currentTitle,
    currentChatId,
    currentChatPinned = false,
    showTTSSettings,
    onToggleTTSSettings,
    onNewChat,
    onShowGuide,
    onChatUpdated,
    onChatDeleted,
    onExportPDF,
    onCopyToClipboard,
    isLive = false
}) => {
    const navigate = useNavigate();
    const { currentProject, projects, fetchProjects } = useProjects();
    const { theme } = useTheme();
    const [showMenu, setShowMenu] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showAddToProjectModal, setShowAddToProjectModal] = useState(false);
    const [showMoveToProjectModal, setShowMoveToProjectModal] = useState(false);
    const menuRef = useRef(null);

    // Click outside to close menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleRenameChat = async (newTitle) => {
        try {
            const { apiPatch } = await import('../../../../utils/api');
            const response = await apiPatch(`/chats/${currentChatId}`, {
                chat: { title: newTitle }
            });
            if (response.ok) {
                setShowRenameModal(false);
                setShowMenu(false);
                if (onChatUpdated) onChatUpdated();
            }
        } catch (error) {
            console.error('Failed to rename chat:', error);
        }
    };

    const handleTogglePin = async () => {
        try {
            const { apiPost } = await import('../../../../utils/api');
            const response = await apiPost(`/chats/${currentChatId}/toggle_pin`);
            if (response.ok) {
                setShowMenu(false);
                if (onChatUpdated) onChatUpdated();
            }
        } catch (error) {
            console.error('Failed to toggle pin:', error);
        }
    };

    const handleAddToProject = async (projectId) => {
        try {
            const { apiPatch } = await import('../../../../utils/api');
            const response = await apiPatch(`/chats/${currentChatId}`, {
                chat: { project_id: projectId }
            });
            if (response.ok) {
                setShowAddToProjectModal(false);
                setShowMenu(false);
                if (onChatUpdated) onChatUpdated();
                await fetchProjects();
            }
        } catch (error) {
            console.error('Failed to add to project:', error);
        }
    };

    const handleMoveToProject = async (projectId) => {
        try {
            const { apiPatch } = await import('../../../../utils/api');
            const response = await apiPatch(`/chats/${currentChatId}`, {
                chat: { project_id: projectId }
            });
            if (response.ok) {
                setShowMoveToProjectModal(false);
                setShowMenu(false);
                if (onChatUpdated) onChatUpdated();
                await fetchProjects();
            }
        } catch (error) {
            console.error('Failed to move to project:', error);
        }
    };

    const handleDeleteChat = async () => {
        if (!window.confirm('Are you sure you want to delete this chat?')) return;

        try {
            const { apiDelete } = await import('../../../../utils/api');
            const response = await apiDelete(`/chats/${currentChatId}`);
            if (response.ok) {
                setShowMenu(false);
                if (onChatDeleted) onChatDeleted();
            }
        } catch (error) {
            console.error('Failed to delete chat:', error);
        }
    };

    return (
        <div className="p-6 sm:p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 bg-white dark:bg-gray-900 pr-12 sm:pr-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 flex-shrink-0" />

                {/* Breadcrumb navigation when in a project */}
                {currentProject ? (
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            onClick={() => navigate('/dashboard/projects')}
                            className="text-sm font-rasa text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                            Projects
                        </button>
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <button
                            onClick={() => navigate(`/dashboard/projects/${currentProject.id}`)}
                            className="text-sm font-rasa text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors truncate"
                        >
                            {currentProject.name}
                        </button>
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-rasa text-gray-700 dark:text-gray-300 truncate">
                            {currentTitle}
                        </span>
                        {isLive && (
                            <span className="flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 flex-shrink-0">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                <span className="text-xs font-rasa font-semibold text-green-700 dark:text-green-400">Live</span>
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg sm:text-xl font-dm-serif truncate text-gray-800 dark:text-gray-100">
                            {currentTitle}
                        </h2>
                        {isLive && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 flex-shrink-0">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                <span className="text-xs font-rasa font-semibold text-green-700 dark:text-green-400">Live</span>
                            </span>
                        )}
                    </div>
                )}

                {/* Chat actions dropdown */}
                {currentChatId && (
                    <div className="relative flex-shrink-0 ml-2" ref={menuRef}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowMenu(!showMenu);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                                theme === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
                            }`}
                            title="Chat actions"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>

                        {showMenu && (
                            <div className={`absolute left-0 mt-2 w-48 rounded-lg shadow-lg border z-50 ${
                                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}>
                                <button
                                    onClick={() => {
                                        setShowRenameModal(true);
                                        setShowMenu(false);
                                    }}
                                    className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <Edit2 className="w-4 h-4" />
                                    Rename
                                </button>
                                <button
                                    onClick={handleTogglePin}
                                    className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    {currentChatPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                    {currentChatPinned ? 'Unpin' : 'Pin'}
                                </button>
                                {currentProject ? (
                                    <button
                                        onClick={() => {
                                            setShowMoveToProjectModal(true);
                                            setShowMenu(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <ArrowRightLeft className="w-4 h-4" />
                                        Move to Project
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setShowAddToProjectModal(true);
                                            setShowMenu(false);
                                        }}
                                        className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <FolderPlus className="w-4 h-4" />
                                        Add to Project
                                    </button>
                                )}

                                {/* Share submenu */}
                                <div className="relative">
                                    <button
                                        onClick={() => setShowShareMenu(!showShareMenu)}
                                        className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Share2 className="w-4 h-4" />
                                            Share
                                        </div>
                                        <ChevronRight className={`w-3 h-3 transition-transform ${showShareMenu ? 'rotate-90' : ''}`} />
                                    </button>

                                    {showShareMenu && (
                                        <div className={`absolute left-full top-0 ml-1 w-48 rounded-lg shadow-lg border ${
                                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                        }`}>
                                            <button
                                                onClick={() => {
                                                    onExportPDF?.();
                                                    setShowMenu(false);
                                                    setShowShareMenu(false);
                                                }}
                                                className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <FileDown className="w-4 h-4" />
                                                Export as PDF
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onCopyToClipboard?.();
                                                    setShowMenu(false);
                                                    setShowShareMenu(false);
                                                }}
                                                className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors font-rasa ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <Copy className="w-4 h-4" />
                                                Copy to Clipboard
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleDeleteChat}
                                    className={`w-full flex items-center gap-2 px-4 py-2 text-left text-red-500 transition-colors font-rasa ${
                                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                )}

            </div>
            
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Guide Button */}
                <button
                    onClick={onShowGuide}
                    className="p-1.5 sm:p-2 rounded-lg transition-colors bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                    title="Guide"
                >
                    <BookOpen size={14} className="sm:w-4 sm:h-4" />
                </button>

                {/* Prompt Settings Button */}
                <button
                    onClick={() => navigate('/dashboard/settings', { state: { fromChat: true } })}
                    className="p-1.5 sm:p-2 rounded-lg transition-colors bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
                    title="Manage Prompts"
                >
                    <Settings size={14} className="sm:w-4 sm:h-4" />
                </button>

                {/* TTS Settings Button */}
                <button
                    onClick={onToggleTTSSettings}
                    className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                        showTTSSettings
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}
                    title="Voice Settings"
                >
                    <Volume2 size={14} className="sm:w-4 sm:h-4" />
                </button>
                
                {/* New Chat Button */}
                <button 
                    onClick={onNewChat}
                    className="px-2 py-1 sm:px-3 sm:py-1 bg-purple-100/ hover:bg-purple-200/ dark:bg-purple-900/10 dark:hover:bg-purple-900/50 border text-purple-700 dark:text-purple-300 rounded-md flex items-center gap-1 transition-colors text-xs sm:text-sm font-dm-serif flex-shrink-0"
                >
                    <Plus size={14} className="sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline">New Chat</span>
                    <span className="xs:hidden">New Chat</span>
                </button>
            </div>

            {/* Modals */}
            <RenameChatModal
                isOpen={showRenameModal}
                onClose={() => setShowRenameModal(false)}
                chatTitle={currentTitle}
                onRename={handleRenameChat}
            />
            <AddToProjectModal
                isOpen={showAddToProjectModal}
                onClose={() => setShowAddToProjectModal(false)}
                projects={projects}
                onAdd={handleAddToProject}
            />
            <MoveToProjectModal
                isOpen={showMoveToProjectModal}
                onClose={() => setShowMoveToProjectModal(false)}
                projects={projects}
                currentProjectId={currentProject?.id}
                onMove={handleMoveToProject}
            />
        </div>
    );
};

export default ChatHeader;